"use client";

import { memo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import equal from "fast-deep-equal";
import { PlotlyChart } from "../PlotlyChart";
import { BokehChart } from "../BokehChart";
import { ImageCarousel } from "../ImageCarousel";
import { ChartCarousel } from "../ChartCarousel";
import { BacktestTabs, type BacktestPayload } from "../BacktestTabs";
import { DashboardView } from "../DashboardView";

interface PlotlyData {
  data: Array<any>;
  layout?: any;
  config?: any;
}

interface BokehData {
  html?: string;
  json?: any;
}

interface ChartOutput {
  type: "plotly" | "bokeh" | "html" | "image" | "backtest-tabs" | "dashboard";
  data: string | PlotlyData | BokehData | BacktestPayload;
}

interface ChartsTabProps {
  chartOutputs: ChartOutput[];
  isReplay?: boolean;
}


// Helper component to render HTML and execute embedded scripts
const ScriptedHtml = ({ html }: { html: string }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    
    // Create a range to parse the HTML and execute scripts
    const range = document.createRange();
    range.selectNode(ref.current);
    const fragment = range.createContextualFragment(html);
    
    ref.current.innerHTML = '';
    ref.current.appendChild(fragment);
  }, [html]);

  return <div ref={ref} className="w-full" />;
};

function ChartsTabComponent({ chartOutputs, isReplay = false }: ChartsTabProps) {
  // Log dashboard outputs for debugging
  const dashboardOutputs = chartOutputs.filter(o => o.type === "dashboard");
  if (dashboardOutputs.length > 0) {
  }

  // Single chart/image - render without carousel
  if (chartOutputs.length === 1) {
    const output = chartOutputs[0];
    return (
      <motion.div
        key="charts"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="rounded-lg w-full max-w-full min-w-0 overflow-hidden bg-card"
      >
        <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <div className="rounded-lg">
            {output.type === 'image' ? (
              <ImageCarousel images={[output.data as string]} />
            ) : output.type === "dashboard" ? (
              <DashboardView html={output.data as string} noBorder={true} isReplay={isReplay} />
            ) : output.type === "backtest-tabs" ? (
              <BacktestTabs data={output.data as BacktestPayload} />
            ) : output.type === "plotly" ? (
              <PlotlyChart
                data={output.data as PlotlyData}
                noBorder={true}
              />
            ) : output.type === "html" ? (
              <ScriptedHtml html={output.data as string} />
            ) : (
              <BokehChart
                data={output.data as string | BokehData}
                noBorder={true}
              />
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Multiple charts/images - render with carousel
  return (
    <motion.div
      key="charts-carousel"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="rounded-lg w-full max-w-full min-w-0 overflow-hidden bg-card"
    >
      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <ChartCarousel
          items={chartOutputs.map((output, index) => (
            <div key={index} className="w-full">
              {output.type === 'image' ? (
                <ImageCarousel images={[output.data as string]} />
              ) : output.type === "dashboard" ? (
                <DashboardView html={output.data as string} noBorder={true} isReplay={isReplay} />
              ) : output.type === "backtest-tabs" ? (
                <BacktestTabs data={output.data as BacktestPayload} />
              ) : output.type === 'plotly' ? (
                <PlotlyChart
                  data={output.data as PlotlyData}
                  noBorder={true}
                />
              ) : output.type === "html" ? (
                <ScriptedHtml html={output.data as string} />
              ) : (
                <BokehChart
                  data={output.data as string | BokehData}
                  noBorder={true}
                />
              )}
            </div>
          ))}
        />
      </div>
    </motion.div>
  );
}

// Memoize with deep equality check
export const ChartsTab = memo(ChartsTabComponent, (prevProps, nextProps) => {
  return equal(prevProps.chartOutputs, nextProps.chartOutputs) && prevProps.isReplay === nextProps.isReplay;
});
